import type { BackEndValueType, ISymbol, IWFParameter } from "@kuika/kuika-cl-model";
import {
  findManagedTaskScreenLink,
  type IManagedTaskScreenLink,
  type IManagedTaskScreenProjectLike
} from "./managed-task-screen";
import { buildProcessInstanceSymbol, buildWorkflowParamRefSymbol } from "./workflow-symbol-factory";

/**
 * KOA-18136 — the values a PLATFORM-MANAGED workflow task screen already carries,
 * and which of them an action parameter may be bound to.
 *
 * ## Why this module exists
 *
 * A task screen receives no screen inputs and hosts no repeater, so the usual
 * binding sources (`screenInput:`, `current.*`) are empty there. What it DOES
 * carry is the running process itself: the platform loads the step instance's
 * parameter bag into the screen before anything else runs (the code generator
 * injects `LoadWorkflowStepInstanceParameters` at the head of `pageInit` for
 * every screen linked through `project.processStepScreenIds`), and every value
 * in that bag is readable — the process instance id, the step instance id, who
 * started the process, and each of the workflow's own parameters.
 *
 * The symbol picker already offers exactly these under "Process → Common /
 * Business Object / Internal" when a managed task screen is open
 * (`designer-symbol-picker-engine.tsx` `generateWorkFlowBagItems`). Until now
 * that offer existed ONLY inside the cascader UI: nothing programmatic could
 * enumerate it, and `buildDesignerSymbol` had no `bindingType` for it. So an
 * action attached to a task screen by any headless path (the refinement pass,
 * the Wirer, the Binder) shipped with its parameters unbound — the live
 * 2026-08-11 smoke put `ListRequesterPreviousExpenseRequests` on a button and
 * left its `ProcessInstanceId` empty, which the placeholder pass then filled
 * with `""`. The query was correct; it was called with nothing.
 *
 * This module is the enumerable half of that offer. It reads the SAME project
 * state the picker reads, and hands each source to the SAME symbol factory the
 * workflow DSL uses, so a binding written from here is indistinguishable from
 * one a user clicked together.
 *
 * ## Scope
 *
 * Managed task screens only. On an ordinary screen the inventory is empty and
 * `bindingType: "processParameter"` is refused — a process-bag read there
 * resolves to nothing at runtime, so accepting it would create exactly the
 * silent dead binding this module exists to prevent.
 */

/** Which family a source belongs to — drives the message, not the symbol. */
export type ManagedBindingSourceKind = "process-instance" | "business-object" | "internal";

/** One value a managed task screen can feed an action parameter from. */
export interface IManagedBindingSource {
  /**
   * The stable key a caller binds by (`bindingType: "processParameter"`,
   * `inputName: <key>`).
   *
   * For a process-instance value this is the platform keyword the code
   * generator switches on (`processInstance`, `workflowStepInstanceId`,
   * `processInitiator`, `processInitiatorFullName`). For a workflow parameter
   * it is the parameter's ID, because that is what codegen resolves the value
   * through — the NAME lives in `label`.
   */
  readonly key: string;
  /** Human-facing name — the picker leaf label, and the field validation keys on. */
  readonly label: string;
  readonly kind: ManagedBindingSourceKind;
  /** Backend value type of the source. */
  readonly type: string;
}

/**
 * Structural subset of a workflow this module reads.
 *
 * Deliberately not `IWorkFlow`: the same id+parameters pair arrives as three
 * different declared types depending on where it was fetched from — a list DTO
 * (`IWorkFlowBaseInfo`), a project-scoped DTO (`IWorkFlowDto`), or the full
 * `IWorkFlow` — and a structural shape accepts all three without a cast.
 */
export interface IManagedBindingWorkflowLike {
  id?: string;
  parameters?: IWFParameter[];
}

/** Structural subset of a project entry carrying its workflows. */
export interface IManagedBindingProjectWithWorkflows {
  id?: string;
  workflows?: IManagedBindingWorkflowLike[];
}

/**
 * Everything the inventory reads, resolved by the caller.
 *
 * Mirrors what `generateWorkFlowBagItems` reads off `this.props.sd`: the flat
 * project (for `processStepScreenIds`), the current screen id, and the
 * workflow data. `selectedWf` is accepted as a fallback for the same reason
 * `designer-workflow-start-params.ts` prefers it — list DTOs omit `parameters`,
 * a full Get carries them.
 */
export interface IManagedBindingSourceContext {
  project?: IManagedTaskScreenProjectLike & { id?: string };
  screenId?: string;
  projectsWithWFData?: IManagedBindingProjectWithWorkflows[];
  workFlowList?: IManagedBindingWorkflowLike[];
  selectedWf?: IManagedBindingWorkflowLike;
  /**
   * A workflow the CALLER fetched because designer state could not supply one
   * carrying `parameters` (KOA-18136).
   *
   * Every in-memory source is conditional: `projectsWithWFData` is filled by
   * three property panels and stays `[]` in a headless turn, `workFlowList`
   * holds list DTOs whose `json` — and therefore `parameters` — is absent, and
   * `selectedWf` exists only while that workflow is open in the modeler. So the
   * inventory silently collapsed to the four platform values whenever the AI
   * touched a task screen without the modeler holding its process, and every
   * action parameter that needed a workflow value went out unbound. Consulted
   * FIRST because a caller only fetches after the local sources came up short.
   */
  fetchedWorkflow?: IManagedBindingWorkflowLike;
}

/**
 * The live designer state this inventory is read from.
 *
 * Structural on purpose — the flat SD state (`getValues(sd)`) satisfies it
 * without the module having to import the context type, which keeps it pure and
 * unit-testable from a plain object.
 */
export interface IManagedBindingSdStateLike {
  project?: IManagedTaskScreenProjectLike & { id?: string };
  sdId?: string;
  projectsWithWFData?: IManagedBindingProjectWithWorkflows[];
  workFlowList?: IManagedBindingWorkflowLike[];
  selectedWf?: IManagedBindingWorkflowLike;
}

/**
 * A workflow supplied for the duration of ONE synchronous call.
 *
 * The write side of a binding (`buildDesignerSymbol`'s `processParameter` branch)
 * is synchronous all the way down through the picker engine, so it cannot fetch;
 * it resolves the inventory from designer state and refuses what it cannot find.
 * In a headless turn that state is empty — `projectsWithWFData` is filled by
 * three property panels only, `workFlowList` carries list DTOs with no
 * parameters, and `selectedWf` exists only while the modeler holds the process —
 * so every workflow-parameter binding failed closed while the READ side, which
 * does fetch, offered the very same parameter as available (KOA-18136, live
 * 2026-08-13: `KUser_Id` left empty on both task screens).
 *
 * Rather than make the whole write path async (the picker engine, the symbol
 * builders and every one of their callers), the caller that CAN fetch supplies
 * the workflow around the retry. Scoped to the synchronous call and always
 * cleared, so nothing observes it afterwards and no stale copy can accumulate.
 */
let callScopedWorkflow: IManagedBindingWorkflowLike | undefined;

/** Run `body` with `workflow` visible to the inventory resolution inside it. */
export function withLoadedProcessWorkflow<T>(workflow: IManagedBindingWorkflowLike | undefined, body: () => T): T {
  const previous: IManagedBindingWorkflowLike | undefined = callScopedWorkflow;
  callScopedWorkflow = workflow;
  try {
    return body();
  } finally {
    callScopedWorkflow = previous;
  }
}

/** Adapt the flat designer state to the inventory's context. */
export function managedBindingContextFromSdState(sd: IManagedBindingSdStateLike): IManagedBindingSourceContext {
  return {
    ...(sd.project !== undefined ? { project: sd.project } : {}),
    ...(sd.sdId !== undefined ? { screenId: sd.sdId } : {}),
    ...(sd.projectsWithWFData !== undefined ? { projectsWithWFData: sd.projectsWithWFData } : {}),
    ...(sd.workFlowList !== undefined ? { workFlowList: sd.workFlowList } : {}),
    ...(sd.selectedWf !== undefined ? { selectedWf: sd.selectedWf } : {}),
    ...(callScopedWorkflow !== undefined ? { fetchedWorkflow: callScopedWorkflow } : {})
  };
}

/**
 * The process-instance values every managed task screen carries.
 *
 * Label and key are pinned to the picker's own leaves — `label` becomes the
 * symbol's `fieldName`, which is what `SymbolModel.IsEmpty()` checks for this
 * category, and `key` becomes `inputName`, which is what the generators switch
 * on. Diverging on either produces a binding that generates correct code and
 * still reports itself unbound (KOA-17362 learned this the hard way).
 */
const PROCESS_INSTANCE_SOURCES: readonly IManagedBindingSource[] = [
  { key: "processInstance", label: "ProcessInstanceId", kind: "process-instance", type: "String" },
  { key: "workflowStepInstanceId", label: "StepInstanceId", kind: "process-instance", type: "String" },
  {
    key: "processInitiator",
    label: "Process Initiator Email/Username",
    kind: "process-instance",
    type: "String"
  },
  {
    key: "processInitiatorFullName",
    label: "Process Initiator FullName",
    kind: "process-instance",
    type: "String"
  }
];

/** The workflow a managed screen's step belongs to, with its parameters loaded. */
function resolveWorkflow(
  ctx: IManagedBindingSourceContext,
  link: IManagedTaskScreenLink
): IManagedBindingWorkflowLike | undefined {
  const processId: string | undefined = link.processId;
  if (processId === undefined || processId.length === 0) return undefined;
  const carriesParameters = (wf: IManagedBindingWorkflowLike | undefined): boolean =>
    wf !== undefined && wf.id === processId && Array.isArray(wf.parameters);

  // What the caller fetched on purpose beats anything that merely happens to be
  // in state.
  if (carriesParameters(ctx.fetchedWorkflow)) return ctx.fetchedWorkflow;

  // A full Get is the only source guaranteed to carry `parameters` (list DTOs
  // omit the json blob), so prefer it when it IS this screen's workflow.
  if (carriesParameters(ctx.selectedWf)) return ctx.selectedWf;

  const projects: IManagedBindingProjectWithWorkflows[] = ctx.projectsWithWFData ?? [];
  for (const project of projects) {
    const match: IManagedBindingWorkflowLike | undefined = (project.workflows ?? []).find(carriesParameters);
    if (match !== undefined) return match;
  }
  return (ctx.workFlowList ?? []).find(carriesParameters);
}

/**
 * Every value an action parameter on THIS screen may be bound to, or an empty
 * list when the screen is not a managed task screen.
 *
 * Order is deliberate and stable: the process-instance values first (they are
 * the ones a headless caller matches by name), then the workflow's own
 * business-object parameters, then its internal ones.
 */
export function listManagedScreenBindingSources(ctx: IManagedBindingSourceContext): IManagedBindingSource[] {
  return resolveManagedScreenBindingSources(ctx).sources;
}

/**
 * The inventory PLUS what it could not resolve locally.
 *
 * The plain list cannot express the difference between "this workflow declares
 * no parameters" and "no source in designer state carries them", and those two
 * demand opposite responses: the first is the answer, the second is a fetch.
 * Reporting `workflowParametersMissing` alongside `processId` lets the caller
 * close the gap instead of shipping a truncated inventory as if it were
 * complete — which is how a task screen's actions went out unbound whenever the
 * modeler was not holding the process (KOA-18136).
 */
export interface IManagedBindingSourceResolution {
  readonly sources: IManagedBindingSource[];
  /** The workflow this screen's step belongs to — absent on an ordinary screen. */
  readonly processId?: string;
  /** True when the screen IS managed but no local source carried the workflow's parameters. */
  readonly workflowParametersMissing: boolean;
}

/**
 * The inventory a screen running THIS workflow's step carries: the four platform
 * values every managed task screen has, plus the workflow's own parameters.
 *
 * Split out so it can be produced from a workflow the CALLER named, not only
 * from one resolved through the browser's screen→step registry — see
 * {@link composeManagedScreenBindingSources}'s use in the MCP handler.
 */
export function composeManagedScreenBindingSources(
  workflow: IManagedBindingWorkflowLike | undefined
): IManagedBindingSource[] {
  const out: IManagedBindingSource[] = [...PROCESS_INSTANCE_SOURCES];
  const parameters: IWFParameter[] = workflow?.parameters ?? [];
  const seen: Set<string> = new Set<string>(out.map((s: IManagedBindingSource): string => s.key));

  for (const parameter of parameters) {
    const key: string = (parameter.id ?? "").trim();
    const label: string = (parameter.name ?? "").trim();
    // Both halves are load-bearing (id → codegen, name → validation); a
    // parameter missing either cannot produce a usable symbol.
    if (key.length === 0 || label.length === 0 || seen.has(key)) continue;
    seen.add(key);
    out.push({
      key,
      label,
      kind: parameter.isBusinessObject === true ? "business-object" : "internal",
      type: String(parameter.dataType ?? "String")
    });
  }
  return out;
}

export function resolveManagedScreenBindingSources(ctx: IManagedBindingSourceContext): IManagedBindingSourceResolution {
  const link: IManagedTaskScreenLink | null = findManagedTaskScreenLink(ctx.project, ctx.screenId);
  if (link === null) return { sources: [], workflowParametersMissing: false };

  const workflow: IManagedBindingWorkflowLike | undefined = resolveWorkflow(ctx, link);
  const out: IManagedBindingSource[] = composeManagedScreenBindingSources(workflow);
  return {
    sources: out,
    ...(link.processId !== undefined && link.processId.length > 0 ? { processId: link.processId } : {}),
    workflowParametersMissing: workflow === undefined
  };
}

/** A workflow as the backend returns it — the only field this module needs. */
export interface IManagedBindingWorkflowDtoLike {
  id?: string;
  json?: string;
}

/**
 * Project a fetched workflow DTO onto the shape the inventory reads.
 *
 * Mirrors the parameters branch of the provider's `convertWFDtoToWf`, including
 * its `isBusinessObject` normalization: older saved workflows omit the flag on
 * internal parameters, and this module branches on `=== true`, so an undefined
 * would silently reclassify a business object as internal. Returns `undefined`
 * for a DTO with no `json` — a list DTO carries no parameters, and pretending
 * otherwise would report an empty parameter set as the answer.
 */
export function managedBindingWorkflowFromDto(
  dto: IManagedBindingWorkflowDtoLike | null | undefined
): IManagedBindingWorkflowLike | undefined {
  if (!dto || typeof dto.json !== "string" || dto.json.length === 0) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(dto.json);
  } catch {
    return undefined;
  }
  const rawParameters: unknown =
    parsed !== null && typeof parsed === "object" ? (parsed as { parameters?: unknown }).parameters : undefined;
  if (!Array.isArray(rawParameters)) return undefined;
  const parameters: IWFParameter[] = (rawParameters as IWFParameter[]).map((parameter: IWFParameter): IWFParameter => ({
    ...parameter,
    isBusinessObject: parameter.isBusinessObject === true
  }));
  return { ...(dto.id !== undefined ? { id: dto.id } : {}), parameters };
}

/** Look one source up by the key a caller binds with. */
export function findManagedScreenBindingSource(
  ctx: IManagedBindingSourceContext,
  key: string
): IManagedBindingSource | undefined {
  const wanted: string = (key ?? "").trim();
  if (wanted.length === 0) return undefined;
  const sources: IManagedBindingSource[] = listManagedScreenBindingSources(ctx);
  const exact: IManagedBindingSource | undefined = sources.find(
    (s: IManagedBindingSource): boolean => s.key === wanted
  );
  if (exact !== undefined) return exact;
  // A caller that only knows the human name (the picker leaf, or a parameter
  // name copied out of the inventory) still addresses a real source.
  return sources.find((s: IManagedBindingSource): boolean => s.label === wanted);
}

/**
 * The symbol for one source, in the exact shape the picker produces for the
 * same leaf.
 *
 * `targetType` is the BOUND-TO side's declared type (the action parameter's),
 * carried so a String process value feeding a Guid parameter records the
 * conversion the same way a hand-picked binding does.
 */
export function buildManagedScreenBindingSymbol(source: IManagedBindingSource, targetType?: string): ISymbol {
  if (source.kind === "process-instance") {
    return buildProcessInstanceSymbol({
      inputName: source.key,
      label: source.label,
      type: source.type,
      ...(targetType !== undefined && targetType.length > 0 ? { targetType } : {})
    });
  }
  return buildWorkflowParamRefSymbol({
    name: source.label,
    id: source.key,
    dataType: source.type,
    ...(targetType !== undefined && targetType.length > 0 ? { targetType } : {})
  });
}

/** Type-narrowing helper for callers that hold a loosely-typed value type. */
export function asBackEndValueType(value: string): BackEndValueType {
  return value as BackEndValueType;
}
